/*
 * Copyright (c) 2022 Certusoft, Inc. All Rights Reserved.
 *
 * This SOURCE CODE FILE, which has been provided by Certusoft as part
 * of a Certusoft product for use ONLY by licensed users of the product,
 * includes CONFIDENTIAL and PROPRIETARY information of Certusoft.
 *
 * USE OF THIS SOFTWARE IS GOVERNED BY THE TERMS AND CONDITIONS
 * OF THE LICENSE STATEMENT AND LIMITED WARRANTY FURNISHED WITH
 * THE PRODUCT.
 */

import { Component, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import * as _ from 'lodash'
import { MatrixFunctions } from '../../models/matrix-functions'
import * as math from 'mathjs'
import { resolveReferences } from '../../../JsonReferenceResolver'
import { NgbPanelChangeEvent } from '@ng-bootstrap/ng-bootstrap'
import { FeatureHintInputComponent } from '../feature-hint-input/feature-hint-input.component'
import { AuthService } from '../../services/auth/auth.service'
import { EntityType, HolePatternType, threedMode } from 'src/app/types/enums'
import { ClikProjectInfo } from '../../types/clik/ClikProjectInfo'

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'app-threed',
    templateUrl: './threed.component.html',
    styleUrls: ['./threed.component.less'],
    providers: [],
})
export class ThreedComponent implements OnInit {
    constructor(private $http: HttpClient, private authService: AuthService, private sanitizer: DomSanitizer) {}

    public configuration: any = {}
    public rendererURL: SafeResourceUrl = this.trustURL('')
    public rawRendererURL: string = ''
    public initialized: boolean = false
    public entityType = EntityType
    public threedMode = threedMode
    public mode: threedMode = threedMode.DATA_MANAGER

    public assemblyKey = ''
    public assembly: any = ''

    public currentPart = ''
    public currentPartInput = ''
    public partsList: any[] = []
    public filteredPartsList: any[] = []
    public partFocused = false
    public partInfo: any = {
        preTransform: {
            up: '',
            front: '',
        },
    }
    public subcatInfo: any = {}
    public axes = ['', 'X', 'Y', 'Z']
    public preTransform: any = []
    public preTransformName: any = ''

    public currentSubcategory = ''
    public currentSubcategoryInput = ''
    public subcategoriesList: any[] = []
    public selectedSubcategories: String[] = []
    public subcatFocused = false
    public filteredSubcategoriesList: any[] = []

    public collapsed: boolean = false
    public transparent: boolean = false

    public activePanelDatum: any = null

    private NOOP_FEATURE = '\nfront'
    private ROUND_PLACE: number = 3 // Default 3

    public featurePromise: any
    private featurePromiseStale: boolean = false
    private featureResolveFn: any
    private featureLocked: boolean = true

    public loading: boolean = false
    public partsLoading: boolean = false
    public subcategoriesLoading: boolean = false
    public disablePreTransform: boolean = false

    public dataManagerInfo: ClikProjectInfo = new ClikProjectInfo()

    @ViewChildren('featureInput')
    public featureInputs!: QueryList<FeatureHintInputComponent>
    public featureInput!: FeatureHintInputComponent

    ngOnInit(): void {
        this.getFeatureRoundingPlaces()
        // Kick off the renderer init process
        this.activate()
        // Start by setting the mode
        this.updateMode(threedMode.DATA_MANAGER)
    }

    public ngAfterViewInit(): void {
        // triggers whenever the FeatureHintInputComponents view child(ren) changes
        this.featureInputs.changes.subscribe((comps: QueryList<FeatureHintInputComponent>) => {
            this.featureInput = comps.first
        })
    }

    // We'll use this to trust our renderer URL (provided from the server)
    trustURL(url: string) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url)
    }

    private renderMessageListener = (ev: MessageEvent<any>) => {
        let vm = this
        vm.rendererMessageReceived(ev, vm)
    }

    activate() {
        let vm = this
        // Listen for events from the 3d iframe
        window.addEventListener('message', this.renderMessageListener)

        this.getConfiguration()
    }

    deactivate() {
        let vm = this
        // Tear down our listener (in case it wasn't done earlier)
        window.removeEventListener('message', this.renderMessageListener)
    }

    // Clean up when the controller gets nuked
    ngOnDestroy() {
        this.deactivate()
    }

    changePart(part: string) {
        this.featureLocked = true
        this.featurePromise = null
        this.currentPart = part
        this.currentPartInput = part
        this.partInfo = {
            preTransform: {
                up: '',
                front: '',
            },
        }
        this.updatePart()
    }

    updatePart() {
        this.getSubcategoriesList()
        if (this.currentPart) {
            // Skip if nothing is selected
            this.getDrawing()
            this.getAssembly()
        }
    }

    updatePartInput() {
        let vm = this
        vm.filteredPartsList = _.filter(vm.partsList, function (x) {
            return x.toLowerCase().includes(vm.currentPartInput.toLowerCase())
        })
    }

    updateSubcat(subcat: string) {
        let vm = this
        vm.currentPart = ''
        vm.currentPartInput = ''
        vm.partInfo = vm.createPartInfo()
        vm.currentSubcategory = subcat
        vm.currentSubcategoryInput = subcat
        vm.getSubcatInfo()
        vm.getPartList(vm.currentSubcategory)
    }

    updateSubcatInput() {
        let vm = this
        vm.filteredSubcategoriesList = _.filter(vm.subcategoriesList, function (x) {
            return x.toLowerCase().includes(vm.currentSubcategoryInput.toLowerCase())
        })
    }

    updateMode(mode: threedMode) {
        let vm = this
        // Set Mode
        vm.mode = mode
        vm.reset()
        // Fetch Data
        switch (mode) {
            case threedMode.VIEW:
            case threedMode.DATUM:
                vm.getPartList()
                break
            case threedMode.SUBCAT:
                vm.getSubcategoriesList()
                break
            case threedMode.DATA_MANAGER:
                break
        }
    }

    reset(full?: boolean) {
        let vm = this
        // Clear values
        vm.currentPart = ''
        vm.currentPartInput = ''
        vm.currentSubcategory = ''
        vm.currentSubcategoryInput = ''
        vm.preTransformName = ''
        vm.preTransform = []
        vm.partInfo = vm.createPartInfo()
        vm.clearScene()

        if (full) {
            vm.partsList = []
            vm.filteredPartsList = []
            vm.subcategoriesList = []
            vm.filteredSubcategoriesList = []
        }
    }

    dataManagerChange(info: ClikProjectInfo) {
        let vm = this
        if (null !== info && info.configurationName === 'clikruntime') {
            vm.dataManagerInfo = info
            vm.reset(true)
        }
    }

    /**
     * Renderer message calls
     */

    // Handle messages from the renderer iframe
    rendererMessageReceived(e: any, vm: any) {
        // vm scope of ThreedComponent passed in since the scope here is Window
        let message = e.data

        console.log('[Clik3DWeb] ' + e.data.event + ' event from ' + e.origin)

        if (message.event === 'visualization.ready') {
            // Go ahead and stop listening -- we can ignore the renderer now
            // window.removeEventListener('message', vm.renderMessageListener);

            // Set the ui style (switch this to a settings object someday,
            // instead of a single name string)
            let uiStyle = 'clik-3d-web'

            let iframe = document.getElementById('render-container')
            let iWindow = (<HTMLIFrameElement>iframe).contentWindow

            if (iWindow) {
                // First style message sets the toolbar styles
                iWindow.postMessage(
                    {
                        event: 'ui.style.set',
                        ui_style: uiStyle,
                    },
                    vm.rendererURL.changingThisBreaksApplicationSecurity
                )

                // Send in the assembly
                iWindow.postMessage(
                    {
                        event: 'assembly.load',
                        assembly: vm.assembly,
                    },
                    vm.rawRendererURL
                )
            }
        }

        if (message.event === 'scene.initialized') {
            // Set the ui style (switch this to a settings object someday,
            // instead of a single name string)
            let uiStyle = 'clik-3d-web'

            let iframe = document.getElementById('render-container')
            let iWindow = (<HTMLIFrameElement>iframe).contentWindow

            if (iWindow) {
                // Update style again on initialization to set camera settings
                iWindow.postMessage(
                    {
                        event: 'ui.style.set',
                        ui_style: uiStyle,
                    },
                    vm.rendererURL.changingThisBreaksApplicationSecurity
                )
            }

            // Parse the hints from clik
            _.forEach(vm.partInfo.datumList, function (datum) {
                vm.updateDescription(datum, true)
            })
            vm.updateRenderedFeatures(true)
        }

        if (message.event === 'assembly.initialized') {
            // Grab the assembly key
            vm.assemblyKey = message.key
        }

        if (message.event === 'scene.feature.done') {
            Promise.resolve(vm.featurePromise).then(function () {
                vm.featurePromise = null
                vm.featureResolveFn = null
                if (vm.featurePromiseStale) {
                    vm.featurePromiseStale = false
                    vm.updateRenderedFeatures()
                }
            })

            // Call resolve on the promise
            if (vm.featureResolveFn) {
                vm.featureResolveFn()
            }
        }
    }

    updateRenderedFeatures: Function = _.debounce((unlock?: boolean) => {
        let vm = this

        // First call should come from 'scene.initialized', lock until then
        if (unlock || !vm.featureLocked) {
            vm.featureLocked = false

            if (!vm.partInfo.datumList || vm.partInfo.datumList.length == 0) {
                // Nothing to do
                return
            }

            if (vm.featurePromise) {
                // If there is an existing promise mark it stale
                vm.featurePromiseStale = true
            } else {
                vm.featurePromise = new Promise(function (resolve) {
                    vm.updateRenderedFeaturesImpl()
                    vm.featureResolveFn = resolve
                })
            }
        } else {
            console.warn('updateRenderedFeatures blocked until scene.initialized completed')
        }
    }, 1000)

    updateRenderedFeaturesImpl() {
        let vm = this

        let iframe = document.getElementById('render-container')
        let iWindow = (<HTMLIFrameElement>iframe).contentWindow

        let featureDescriptionMap: any = {}
        _.forEach(vm.partInfo.datumList, function (datum, key: number) {
            datum.descriptionDirty(false)
            datum.transformDirty(false)

            let mapKey = datum.featureId || ''

            let line = '\n'
            if (!featureDescriptionMap[mapKey]) {
                featureDescriptionMap[mapKey] = {
                    description: '',
                    colors: [],
                }
                line = ''
            }

            let saturatedColor = vm.saturate(vm.colorSet[key].rgb, 0.7)

            _.forEach(saturatedColor, function (val, idx) {
                // Adjustments to add transparency to planes.
                let alpha = 1.0
                // If the parsed state is a plane, or if there is no parsed state and the datum type is a plane.
                // If the parsed state is a plane we want to show it as transparent even if the datum type doesn't match.
                if (datum.getEntityType() === EntityType.PLANE) {
                    alpha = 0.3
                }
                featureDescriptionMap[mapKey].colors.push((val / 255) * (idx === 3 ? alpha : 1))
            })

            if (datum.featureDescriptionOverride) {
                line = line + 'transform ' + JSON.stringify(datum.values)
            } else {
                line = line + datum.featureDescription
            }

            // If the line is empty, populate it with a dummy description to ensure the colors remain synced
            if (line.trim() === '' || datum.hidden) {
                line = vm.NOOP_FEATURE
            }

            featureDescriptionMap[mapKey].description = featureDescriptionMap[mapKey].description + line
        })

        let preTransform = FeatureHintInputComponent.calculatePreTransform(
            vm.createVectorFromAxis(vm.partInfo.preTransform.front),
            vm.createVectorFromAxis(vm.partInfo.preTransform.up)
        )

        _.forEach(featureDescriptionMap, function (value, key) {
            let colors = value.colors.length > 0 ? '[' + value.colors.join(',') + ']' : ''
            if (iWindow) {
                iWindow.postMessage(
                    {
                        event: 'scene.feature.update',
                        params: {
                            a: vm.assemblyKey,
                            fx: preTransform[0],
                            fy: preTransform[1],
                            fz: preTransform[2],
                            ux: preTransform[3],
                            uy: preTransform[4],
                            uz: preTransform[5],
                            cs: colors,
                            mf: 1000,
                            n: key || '',
                            featureHint: value.description,
                        },
                    },
                    vm.rawRendererURL
                )
            }
        })
    }

    clearScene() {
        let vm = this
        vm.rendererURL = this.trustURL('')
        vm.rawRendererURL = ''
        vm.initialized = false
    }

    // Reprocess description for datum
    updateDescription(datum: any, suppressUpdate?: boolean) {
        if (!this.assemblyKey) {
            // If we don't have an assemblyKey yet then don't try to parse.
            return
        }

        let vm = this
        datum.loading++

        let hint = ''
        switch (vm.mode) {
            case threedMode.DATUM:
            case threedMode.SUBCAT:
                hint = datum.featureDescription
                break
        }

        return FeatureHintInputComponent.parseFeatureHint(
            this.$http,
            hint,
            this.assemblyKey,
            this.createVectorFromAxis(vm.partInfo.preTransform.front),
            this.createVectorFromAxis(vm.partInfo.preTransform.up),
            datum.featureId,
            false,
            vm.featureInput
        ).then(
            function (response: any) {
                datum.state = response.data
                // Update the status
                if (datum.state.parseSuccessful) {
                    if (datum.state.logicalStructure.entityEvaluationDetails === 'OK') {
                        datum.status = 'OK'
                    } else {
                        datum.status = 'WARNING'
                    }
                } else {
                    datum.status = 'ERROR'
                }

                // Update the transform if applicable
                let datumStateType = datum.getDetectedEntityType()
                datum.entityTypeMatch = datumStateType === datum.getDatumEntityType()

                if (datum?.state?.logicalStructure?.entityEvaluationSuccessful) {
                    switch (datumStateType) {
                        case EntityType.TRANSFORM:
                            vm.updateDatumTransformFromMatrix(datum, datum.state.logicalStructure.entityData)
                            break
                        case EntityType.PLANE:
                            vm.updateDatumTransformFromPlane(datum, datum.state.logicalStructure.entityData, true)
                            break
                        case EntityType.HOLE_PATTERN:
                        case EntityType.ARC_PAIR:
                        case EntityType.SLOT:
                            vm.updateDatumTransformFromHolePattern(datum, datum.state.logicalStructure.entityData)
                            break
                        default:
                            vm.clearDatumTransform(datum)
                    }
                } else {
                    // Clear out entityData, since it is referenced in the UI and we don't want endless rows of
                    // arc or arc pair data crashing the client. If the entityData is to be used for something, do so before this
                    if (datum?.state?.logicalStructure?.entityData) {
                        datum.state.logicalStructure.entityData = []
                    }
                    vm.clearDatumTransform(datum)
                }

                if (!suppressUpdate) {
                    vm.updateRenderedFeatures()
                }

                datum.loading--
                if (datum.loading < 0) {
                    datum.loading = 0
                }
            },
            function (response: any) {
                console.error('Error parsing feature hint...')
                datum.loading--
                if (datum.loading < 0) {
                    datum.loading = 0
                }
            }
        )
    }

    /**
     * API Calls
     */

    // Load the renderer configuration (notably, the target URL)
    getConfiguration() {
        let vm = this
        vm.$http
            .get('/api/renderer/configuration')
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetRendererConfigurationSuccess(response.configuration)
                },
                function (response: any) {
                    console.error('Error getting renderer configuration...')
                }
            )
    }

    // Get the rounding precision
    getFeatureRoundingPlaces() {
        let vm = this
        vm.$http
            .get('/api/clik-feature/feature-rounding-places/')
            .toPromise()
            .then(
                function (response: any) {
                    vm.ROUND_PLACE = response
                },
                function (response: any) {
                    console.error('Error getting rounding places...')
                }
            )
    }

    // Fetch the assembly from CLIK for the current object
    getAssembly() {
        let vm = this
        vm.$http
            .get('/api/clik-feature/feature-assembly/' + vm.currentPart)
            .toPromise()
            .then(
                function (response: any) {
                    response = resolveReferences(response)
                    vm.handleGetAssemblySuccess(response.assembleResult)
                },
                function (response: any) {
                    console.error('Error getting assembly...')
                }
            )
    }

    // Fetch the drawing from CLIK for the current object
    getDrawing() {
        let vm = this
        vm.$http
            .get('/api/clik-feature/drawing/' + vm.currentPart)
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetDrawingSuccess(response)
                },
                function (response: any) {
                    console.error('Error getting drawing...')
                }
            )
        vm.$http
            .get('/api/clik-feature/subcats-for-part/' + vm.currentPart)
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetSubcategoriesForPartSuccess(response)
                },
                function (response: any) {
                    console.error('Error getting subcategories for part...')
                }
            )
    }

    // Fetch the partInfo from CLIK for the current object
    getPartInfo(saveOnSuccess?: boolean) {
        let vm = this
        vm.$http
            .get('/api/clik-feature/feature-info/' + vm.currentPart)
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetPartInfoSuccess(response, false, saveOnSuccess || false)
                    vm.loading = false
                },
                function (response: any) {
                    console.error('Error getting part info...')
                    vm.loading = false
                }
            )
    }

    // Fetch the partInfo from CLIK for the current object
    getSubcatInfo() {
        let vm = this
        vm.$http
            .get('/api/clik-feature/subcat-info/' + vm.currentSubcategory)
            .toPromise()
            .then(
                function (response: any) {
                    vm.handleGetSubcatInfoSuccess(response[0])
                },
                function (response: any) {
                    console.error('Error getting part info...')
                }
            )
    }

    // Fetch the partList from CLIK
    getPartList(subcatName?: any) {
        let vm = this
        vm.partsLoading = true
        vm.$http
            .get('/api/clik-feature/parts-list', {
                params: { subcatName: subcatName || '' },
            })
            .toPromise()
            .then(
                function (response: any) {
                    vm.partsLoading = false
                    vm.handleGetPartsListSuccess(response)
                },
                function (response: any) {
                    vm.partsLoading = false
                    console.error('Error getting parts list...')
                }
            )
    }

    // Fetch the partList from CLIK
    getSubcategoriesList() {
        let vm = this
        vm.subcategoriesLoading = true
        vm.$http
            .get('/api/clik-feature/subcategories-list')
            .toPromise()
            .then(
                function (response: any) {
                    vm.subcategoriesLoading = false
                    vm.handleGetSubcategoriesListSuccess(response)
                },
                function (response: any) {
                    vm.subcategoriesLoading = false
                    console.error('Error getting subcategories list...')
                }
            )
    }

    // Generate the datum entries in Clik based on the part and the selected subcats
    generateDatum(regenerate: boolean, datumName?: string) {
        let vm = this
        vm.loading = true
        let url = regenerate ? '/api/clik-feature/regenerate-datum/' : '/api/clik-feature/generate-datum/'
        vm.$http
            .post(url + vm.currentPart, vm.selectedSubcategories, {
                params: { datumName: datumName || '' },
            })
            .toPromise()
            .then(
                function (response: any) {
                    vm.getPartInfo(true)
                    vm.loading = false
                },
                function (response: any) {
                    vm.loading = false
                    console.error('Error generating datum info...', response)
                }
            )
    }

    // Save datum changes
    saveDatum(datum?: any) {
        let vm = this
        // If no specific datum provided, save them all
        let toUpdate = datum ? [datum] : vm.partInfo.datumList

        vm.loading = true

        switch (this.mode) {
            case threedMode.DATUM:
                // Get the updated pre-transform without committing it yet
                let preTransform = vm.updatePreTransform(false)

                let payload = {
                    datum: toUpdate,
                    partName: vm.currentPart,
                    transform: preTransform,
                }

                vm.$http
                    .put('/api/clik-feature/update-features-with-pre-transform', payload)
                    .toPromise()
                    .then(
                        function (response: any) {
                            vm.getPartInfo()
                        },
                        function (response: any) {
                            console.error('Error saving datum...', response)
                            vm.loading = false
                        }
                    )
                break
            case threedMode.SUBCAT:
                // Make a map from the template values (list of named strings)
                let templateMap: any = {}
                _.forEach(vm.subcatInfo.featureDescriptionTemplateKeys, function (item, idx) {
                    templateMap[item] = vm.subcatInfo.featureDescriptionTemplateValues[idx]
                })
                // Update values in map from datum descriptions
                _.forEach(toUpdate, function (datum) {
                    templateMap[datum.datum.name] = datum.featureDescription
                })
                // Convert map back to list of named strings
                vm.subcatInfo.featureDescriptionTemplateKeys = []
                vm.subcatInfo.featureDescriptionTemplateValues = []
                _.forEach(templateMap, function (value, key) {
                    vm.subcatInfo.featureDescriptionTemplateKeys.push(key)
                    vm.subcatInfo.featureDescriptionTemplateValues.push(value)
                })

                vm.$http
                    .put('/api/clik-feature/update-templates', [vm.subcatInfo])
                    .toPromise()
                    .then(
                        function (response: any) {
                            vm.getPartInfo()
                        },
                        function (response: any) {
                            console.error('Error saving datum...', response)
                            vm.loading = false
                        }
                    )
                break
        }
    }

    // Save datum changes
    savePreTransform(transform: any) {
        let vm = this
        vm.$http
            .put('/api/clik-feature/update-pre-transform/' + vm.currentPart, transform)
            .toPromise()
            .then(
                function (response: any) {
                    vm.getPartInfo()
                },
                function (response: any) {
                    console.error('Error saving pre-transform...', response)
                }
            )
    }

    /**
     * Success handlers
     */

    handleGetAssemblySuccess(assembly: any) {
        let vm = this
        this.assembly = assembly

        if (this.mode !== threedMode.VIEW) {
            this.getPartInfo()
        }

        // Now that we have the assembly, assign the renderer URL to tell the iframe
        // to load the renderer page -- note that the rendererURL variable is assigned
        // to the controller separately from the configuration value on purpose, as
        // otherwise the iframe will load too early
        if (!this.initialized) {
            // If this is the first initialization set the url
            this.rawRendererURL = this.configuration.rendererURL
            this.rendererURL = this.trustURL(this.configuration.rendererURL)
            this.initialized = true
        } else {
            // Otherwise just post the load message
            let iframe = document.getElementById('render-container')
            let iWindow = (<HTMLIFrameElement>iframe).contentWindow

            if (iWindow) {
                // Send in the assembly
                iWindow.postMessage(
                    {
                        event: 'assembly.load',
                        assembly: vm.assembly,
                    },
                    vm.rawRendererURL
                )
            }
        }
    }

    handleGetDrawingSuccess(drawing: any) {
        let vm = this
        let up = ''
        let front = ''

        if (drawing && drawing[0]) {
            let name = drawing[0].drawingTransform.name

            // Make sure the transform name is the right format (e.g. +X-Y)
            let matchesPattern = name.match(/([+\-][X-Z]){2}/g)
            if (matchesPattern) {
                up = name.slice(0, 2)
                front = name.slice(2, 4)
            }
            let options = this.generatePreTransformAxesOptions('')
            vm.disablePreTransform = !matchesPattern || !(options.includes(up) && options.includes(front))

            vm.preTransformName = drawing[0].drawingTransform.name
            vm.preTransform = drawing[0].drawingTransform.values
        }
        vm.partInfo.preTransform = {
            up: up,
            front: front,
        }
    }

    handleGetSubcategoriesForPartSuccess(subcategories: any) {
        let vm = this
        vm.selectedSubcategories = _.map(subcategories, 'name')
    }

    handleGetPartInfoSuccess(partInfo: any, appending: boolean, save: boolean) {
        let vm = this

        partInfo = resolveReferences(partInfo, true)

        if (!this.partInfo) {
            this.partInfo = this.createPartInfo()
        }

        this.partInfo.name = vm.currentPart
        if (!this.partInfo.datumList || !appending) {
            this.partInfo.datumList = []
        }
        this.partInfo.datumList = this.partInfo.datumList.concat(
            _.map(
                _.filter(partInfo, function (datum) {
                    // If we are in subcat mode, filter the datum list down based on the selected subcat
                    return (
                        vm.mode !== threedMode.SUBCAT ||
                        _.some(datum.componentSubcategories, function (subcat) {
                            return subcat.name === vm.currentSubcategory
                        })
                    )
                }),
                function (part) {
                    let datum = vm.createDatum()
                    datum.name = part.name
                    datum.displayName = part.displayName
                    datum.featureDescriptionOverride = part.featureDescriptionOverride
                    datum.featureDescription = part.featureDescriptionOverride ? '' : part.featureDescription
                    datum.featureId = part.featureId
                    datum.datum = part.datum
                    datum.componentSubcategories = part.componentSubcategories

                    // Auto-populate the
                    if (vm.mode === threedMode.SUBCAT) {
                        datum.componentSubcategories = [vm.subcatInfo]

                        // Find the matching template from the subcat
                        let idx = -1
                        _.forEach(vm.subcatInfo.featureDescriptionTemplateKeys, function (key, i) {
                            if (datum.datum.name === key) {
                                idx = parseInt(i)
                            }
                        })

                        // If the template has a value, use that. Otherwise fall back on the datum value
                        if (idx > -1) {
                            datum.featureDescription = vm.subcatInfo.featureDescriptionTemplateValues[idx]
                        } else if (datum.featureDescription) {
                            datum.descriptionDirty(true)
                        }
                    }

                    if (datum.getDatumEntityType() === EntityType.TRANSFORM && part.values.length === 16) {
                        vm.updateDatumTransformFromMatrix(datum, part.values)
                    }
                    if (datum.getDatumEntityType() === EntityType.PLANE && part.values.length === 4) {
                        vm.updateDatumTransformFromPlane(datum, part.values)
                    }

                    return datum
                }
            )
        )

        this.partInfo.datumList = _.sortBy(this.partInfo.datumList, 'name')

        if (this.partInfo.datumList.length > this.colorSet.length) {
            let diff = this.partInfo.datumList.length - this.colorSet.length
            for (let i = 0; i < diff; i++) {
                this.generateColor()
            }
        }

        if (!appending) {
            Promise.all(
                _.map(vm.partInfo.datumList, function (datum) {
                    return vm.updateDescription(datum, true)
                })
            ).then(function () {
                if (save) {
                    vm.saveDatum()
                }
            })
            vm.updateRenderedFeatures()
        }
    }

    handleGetSubcatInfoSuccess(subcatInfo: any) {
        let vm = this
        vm.subcatInfo = resolveReferences(subcatInfo, true)
    }

    handleGetPartsListSuccess(partsList: any) {
        let vm = this
        vm.partsList = _.sortBy(partsList)
        vm.filteredPartsList = _.sortBy(partsList)
    }

    handleGetSubcategoriesListSuccess(subcategoriesList: any) {
        let vm = this
        vm.subcategoriesList = _.sortBy(subcategoriesList)
        vm.filteredSubcategoriesList = _.sortBy(subcategoriesList)
    }

    handleGetRendererConfigurationSuccess(configuration: any) {
        let vm = this
        vm.configuration = configuration

        this.updatePart()
    }

    public onPanelChange($event: NgbPanelChangeEvent, vm: any) {}

    /**
     * Conversion functions
     */

    // Templates
    public infoTemplate = {
        name: '',
        preTransform: {
            up: '',
            front: '',
        },
        datumList: [],
    }
    public datumTemplate = {
        name: '',
        featureDescriptionOverride: false,
        featureDescription: '',
        status: 'OK',
        loading: 0,
        descriptionDirtyVal: false,
        transformDirtyVal: false,
        saveDirtyVal: false,
        values: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
        transformComponents: {
            rotations: [
                {
                    axis: '',
                    angle: 0.0,
                },
            ],
            translateX: 0,
            translateY: 0,
            translateZ: 0,
        },
    }

    createPartInfo() {
        let vm = this
        return JSON.parse(JSON.stringify(vm.infoTemplate))
    }

    createDatum() {
        let vm = this
        let datum = JSON.parse(JSON.stringify(vm.datumTemplate))
        datum.descriptionDirty = (val?: boolean) => {
            if (undefined !== val) {
                datum.descriptionDirtyVal = val
                if (val) {
                    datum.saveDirty(val)
                }
            }
            return datum.descriptionDirtyVal
        }
        datum.transformDirty = (val?: boolean) => {
            if (undefined !== val) {
                datum.transformDirtyVal = val
                if (val) {
                    datum.saveDirty(val)
                }
            }
            return datum.transformDirtyVal
        }
        datum.saveDirty = (val?: boolean) => {
            if (undefined !== val) {
                datum.saveDirtyVal = val
            }
            return datum.saveDirtyVal
        }
        datum.hidden = false
        datum.toggleHidden = () => {
            datum.hidden = !datum.hidden
            vm.updateRenderedFeatures()
        }
        datum.getEntityType = () => {
            // Get the most accurate type available
            return datum.getDetectedEntityType() || datum.getDatumEntityType()
        }
        datum.getDatumEntityType = () => {
            let type = datum.datum?.datumType?.name
            if (type) {
                if (type === 'CSYS') {
                    return EntityType.TRANSFORM
                }
                if (type === 'HOLE') {
                    return EntityType.ARC_PAIR
                }
                return type
            }
            return null
        }
        datum.getDetectedEntityType = () => {
            return datum.state?.logicalStructure?.entityType
        }
        datum.entityTypeMatch = true
        return datum
    }

    createSaveDatum(datum: any) {
        let saveDatum = {}

        return saveDatum
    }

    anySaveDirty() {
        return _.some(this.partInfo.datumList, function (d) {
            return d.saveDirty()
        })
    }

    clearDatumTransform(datum: any) {
        datum.values = []
        datum.transformComponents.rotations = []
        datum.transformComponents.translateX = null
        datum.transformComponents.translateY = null
        datum.transformComponents.translateZ = null
    }

    updateDatumTransformFromMatrix(datum: any, inMatrix: number[]) {
        let vm = this
        if (inMatrix != null && inMatrix.length > 0) {
            let matrix = _.map(inMatrix, (x) => vm.formatNumber(x))
            let mat = math.matrix(matrix)
            datum.values = matrix
            let components = MatrixFunctions.decomposeRotation(mat)
            datum.transformComponents.rotations = _.map(components, function (component, index) {
                return {
                    axis: vm.axes[index + 1],
                    angle: MatrixFunctions.radToDeg(component),
                }
            })
            datum.transformComponents.translateX = mat.get([3])
            datum.transformComponents.translateY = mat.get([7])
            datum.transformComponents.translateZ = mat.get([11])
        }
    }

    updateDatumTransformFromPlane(datum: any, matrix: number[], computeDot?: boolean) {
        let vm = this
        if (matrix != null && matrix.length > 0) {
            // Convert point/normal format into normal/distance format
            if (computeDot && matrix.length > 5) {
                let point = matrix.slice(0, 3)
                let norm = matrix.slice(3, 6)
                let dist = math.dot(point, norm)
                matrix = norm.concat([dist])
            }
            datum.values = _.map(matrix, (x) => vm.formatNumber(x))
        }
    }

    updateDatumTransformFromHolePattern(datum: any, data: any[]) {
        let vm = this
        // Clear values, updateDatumTransformFromSlot will append them
        datum.values = []
        _.forEach(data, function (entry) {
            return vm.updateDatumTransformFromHole(datum, entry, true)
        })
    }

    updateDatumTransformFromHole(datum: any, data: any, append?: boolean) {
        // x,   y,   z,   // slot or hole center
        // dx1, dy1, dz1, // basis vec. 1, oriented in shorter axis direction (length gives radius)
        // dx2, dy2, dz2, // basis vec. 2 perpendicular to basis vector 1, has same length as basis vector 1 if hole
        // dx3, dy3, dz3, // basis vec. 3, perp to basis vectors 1 and 2, length gives half depth
        let center1 = [data.center1.x, data.center1.y, data.center1.z]
        let center2 = [data.center2.x, data.center2.y, data.center2.z]

        let center0: number[] = [
            (center2[0] + center1[0]) / 2,
            (center2[1] + center1[1]) / 2,
            (center2[2] + center1[2]) / 2,
        ]
        let d1: number[]
        let d2: number[]
        let d3: number[] = [center1[0] - center0[0], center1[1] - center0[1], center1[2] - center0[2]]

        // Intermediate vector for doing cross products
        let vec: number[] = []
        switch (data.type) {
            case HolePatternType.HOLE:
                // Find axis with min dot product
                let axes = [
                    [1, 0, 0],
                    [0, 1, 0],
                    [0, 0, 1],
                ]
                vec = _.reduce(
                    axes,
                    function (axis, best) {
                        if (Math.abs(math.dot(d3, best)) > Math.abs(math.dot(d3, axis))) {
                            return axis
                        }
                        return best
                    },
                    d3
                )
                break
            case HolePatternType.SLOT:
                vec = [data.slotDir.x, data.slotDir.y, data.slotDir.z]
                break
        }

        // Cross product of d3 and vec, length is radius
        d1 = math.multiply(MatrixFunctions.normalize(math.cross(d3, vec) as number[]), data.radius)
        // Cross product of d3 and d1, length is radius + 1/2 width
        d2 = math.multiply(MatrixFunctions.normalize(math.cross(d3, d1) as number[]), data.width / 2 + data.radius)

        // Final format:
        // T = [[ dx1, dx2, dx3, x ],
        //      [ dy1, dy2, dy3, y ],
        //      [ dz1, dz2, dz3, z ],
        //      [ 0,   0,   0,   1 ]]

        d1.push(0)
        d2.push(0)
        d3.push(0)
        center0.push(1)
        let tmp = math.transpose(math.matrix([d1, d2, d3, center0]))

        if (!append) {
            datum.values = []
        }

        datum.values.push(...math.flatten(tmp).toArray())
    }

    updateDatumTransformFromComponents(datum: any, componentIdx: number) {
        let vm = this

        // Process component updates
        if (componentIdx >= 0) {
            // Check if it's a rotation axis change
            // Truncate list
            datum.transformComponents.rotations.length = componentIdx + 1
            if (componentIdx < 2) {
                datum.transformComponents.rotations.push({
                    axis: '',
                    angle: 0.0,
                })
            }
        }

        let angles: number[] = _.map(datum.transformComponents.rotations, 'angle')
        let mat = MatrixFunctions.composeRotation(
            _.map(angles, MatrixFunctions.degToRad),
            _.map(datum.transformComponents.rotations, 'axis')
        )

        mat.set([3], datum.transformComponents.translateX)
        mat.set([7], datum.transformComponents.translateY)
        mat.set([11], datum.transformComponents.translateZ)

        datum.values = mat.toArray()
        datum.values = _.map(datum.valuies, (x) => vm.formatNumber(x))
    }

    updatePreTransform(commit: boolean = false) {
        let vm = this

        // If both components are not set just return
        if (vm.partInfo.preTransform.up === '' || vm.partInfo.preTransform.front === '') {
            return
        }

        // Compute transformation to align axes
        let up = this.createVectorFromAxis(vm.partInfo.preTransform.up)
        let front = this.createVectorFromAxis(vm.partInfo.preTransform.front)
        let transform: any[] = []
        let cross = math.cross(up, front) as number[]
        transform.push(up)
        transform.push(front)
        transform.push(cross)

        let defaultCoordinates = math.transpose([
            [0, 0, 1],
            [1, 0, 0],
            [0, 1, 0],
        ])

        transform = math.multiply(defaultCoordinates, transform)
        transform = MatrixFunctions.format(transform)

        vm.preTransformName = vm.partInfo.preTransform.up + vm.partInfo.preTransform.front
        vm.preTransform = transform

        _.forEach(vm.assembly.assembly.subAssemblies, function (sub) {
            if (sub.name === vm.currentPart) {
                sub.transform.values = transform
            }
        })

        let drawingTransform = {
            name: vm.partInfo.preTransform.up + vm.partInfo.preTransform.front, // Concatenation of axes (e.g. +Z+X)
            description:
                'UP: ' +
                vm.partInfo.preTransform.up +
                ', FRONT: ' +
                vm.partInfo.preTransform.front +
                ' [Generated by ClikAI]',
            values: transform,
        }

        // Only save in datum mode if commit specified
        if (commit && vm.mode === threedMode.DATUM) {
            // Save
            vm.savePreTransform(drawingTransform)
        }

        let iframe = document.getElementById('render-container')
        let iWindow = (<HTMLIFrameElement>iframe).contentWindow

        if (iWindow) {
            // Send in the assembly
            iWindow.postMessage(
                {
                    event: 'assembly.update',
                    assembly: vm.assembly,
                },
                vm.rawRendererURL
            )
        }

        return drawingTransform
    }

    toggleTransparency() {
        let vm = this

        vm.transparent = !vm.transparent

        _.forEach(vm.assembly.assembly.subAssemblies, function (sub) {
            if (sub.name === vm.currentPart) {
                sub.baseColor.r = 0.5
                sub.baseColor.g = 0.5
                sub.baseColor.b = 0.5
                sub.baseColor.a = 0.75
                sub.material.name = vm.transparent ? '#0,1,0,0,0.05,0' : 'predefined'
            }
        })

        let iframe = document.getElementById('render-container')
        let iWindow = (<HTMLIFrameElement>iframe).contentWindow

        if (iWindow) {
            // Send in the assembly
            iWindow.postMessage(
                {
                    event: 'assembly.update',
                    assembly: vm.assembly,
                },
                vm.rawRendererURL
            )
        }
    }

    /**
     * Helper functions
     */

    // Visually distinct colors for feature highlighting
    public colorSet = [
        // https://sashamaps.net/docs/resources/20-colors/
        { hex: '#e6194B', rgb: [230.0, 25.0, 25.0, 255.0] }, //Red
        { hex: '#3cb44b', rgb: [60.0, 180.0, 75.0, 255.0] }, //Green
        { hex: '#ffe119', rgb: [255.0, 225.0, 25.0, 255.0] }, //Yellow
        { hex: '#4363d8', rgb: [50.0, 80.0, 230.0, 255.0] }, //Blue
        { hex: '#f58231', rgb: [245.0, 130.0, 49.0, 255.0] }, //Orange
        { hex: '#911eb4', rgb: [145.0, 30.0, 180.0, 255.0] }, //Purple
        { hex: '#42d4f4', rgb: [66.0, 212.0, 244.0, 255.0] }, //Cyan
        { hex: '#bfef45', rgb: [191.0, 239.0, 69.0, 255.0] }, //Lime
        { hex: '#f032e6', rgb: [240.0, 50.0, 230.0, 255.0] }, //Magenta
        { hex: '#fabed4', rgb: [250.0, 190.0, 212.0, 255.0] }, //Pink
        { hex: '#469990', rgb: [70.0, 153.0, 144.0, 255.0] }, //Teal
        { hex: '#800000', rgb: [128.0, 0.0, 0.0, 255.0] }, //Maroon
        { hex: '#000075', rgb: [0.0, 0.0, 117.0, 255.0] }, //Navy
        { hex: '#9A6324', rgb: [154.0, 99.0, 36.0, 255.0] }, //Brown
        { hex: '#808000', rgb: [128.0, 128.0, 0.0, 255.0] }, //Olive
        { hex: '#aaffc3', rgb: [170.0, 255.0, 195.0, 255.0] }, //Mint
        { hex: '#dcbeff', rgb: [220.0, 190.0, 255.0, 255.0] }, //Lavender
        { hex: '#fffac8', rgb: [255.0, 250.0, 200.0, 255.0] }, //Beige
        { hex: '#ffd8b1', rgb: [255.0, 216.0, 177.0, 255.0] }, //Apricot
        { hex: '#a9a9a9', rgb: [169.0, 169.0, 169.0, 255.0] }, //Grey
        { hex: '#ffffff', rgb: [255.0, 255.0, 255.0, 255.0] }, //White
        { hex: '#000000', rgb: [0.0, 0.0, 0.0, 255.0] }, //Black
    ]

    //https://stackoverflow.com/questions/13806483/increase-or-decrease-color-saturation
    saturate(d: number[], value: number) {
        let r = d[0]
        let g = d[1]
        let b = d[2]
        let a = d[3]
        let gray = 0.2989 * r + 0.587 * g + 0.114 * b //weights from CCIR 601 spec
        r = -gray * value + r * (1 + value)
        g = -gray * value + g * (1 + value)
        b = -gray * value + b * (1 + value)
        //normalize over- and under-saturated values
        if (r > 255) r = 255
        if (g > 255) g = 255
        if (b > 255) b = 255
        if (r < 0) r = 0
        if (g < 0) g = 0
        if (b < 0) b = 0
        return [r, g, b, a]
    }

    // Generate a random color in case there are more than the predefined ones needed.
    generateColor() {
        let hex = Math.floor(Math.random() * 16777215)
            .toString(16)
            .padStart(6, '0')
        let rgbParse = hex.match(/.{1,2}/g)
        if (null != rgbParse) {
            let rgb = [parseInt(rgbParse[0], 16), parseInt(rgbParse[1], 16), parseInt(rgbParse[2], 16), 255.0]
            this.colorSet.push({
                hex: '#' + hex,
                rgb: rgb,
            })
        }
    }

    // Add subcat to selected list
    updateSubcategories(subcat: String) {
        this.selectedSubcategories.push(subcat)
        this.currentSubcategory = ''
    }

    // Remove subcat from selected list
    removeSubcategory(idx: number) {
        this.selectedSubcategories.splice(idx, 1)
    }

    // Generate list of subcat options, excluding ones that are already selected
    generateSubcatOptions() {
        let vm = this
        return [''].concat(
            _.filter(vm.subcategoriesList, function (subcat) {
                return !vm.selectedSubcategories.includes(subcat)
            })
        )
    }

    // Generate list of axis options, excluding ones that are already selected
    generateAxesOptions(datum: any, componentIdx: number) {
        let vm = this
        let usedAxes = _.map(datum.transformComponents.rotations, 'axis')
        usedAxes.length = componentIdx
        return _.filter(vm.axes, function (axis) {
            return !usedAxes.includes(axis)
        })
    }

    // Generate list of pre transform axis options, excluding ones that are already selected
    generatePreTransformAxesOptions(selected: string) {
        let vm = this
        let out: string[] = []
        _.forEach(vm.axes, function (axis) {
            // Include blank as an option if the value is currently unset
            if (axis === '' && (vm.partInfo.preTransform.front === '' || vm.partInfo.preTransform.up === '')) {
                out.push(axis)
            } else if (!selected.includes(axis)) {
                out.push('+' + axis)
                out.push('-' + axis)
            }
        })

        return out
    }

    createVectorFromAxis(signedAxis: string) {
        let sign = signedAxis[0] === '-' ? -1 : 1
        let axis = signedAxis[1]
        return [axis === 'X' ? sign : 0, axis === 'Y' ? sign : 0, axis === 'Z' ? sign : 0]
    }

    entityTitle(entityType: string) {
        switch (entityType) {
            case EntityType.TRANSFORM:
                return 'Matrix Transform'
            case EntityType.PLANE:
                return 'Plane Coordinates'
            case EntityType.HOLE_PATTERN:
            case EntityType.ARC_PAIR:
            case EntityType.SLOT:
                return 'Hole Info'
            default:
                return 'Entity Info'
        }
    }

    formatNumber(num: number, places: number = this.ROUND_PLACE) {
        let pow = Math.pow(10, places)
        return Math.round(num * pow) / pow
    }
}
